'use client';
import { useRef, useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useCountUp } from '@/hooks/use-count-up';

const stats = [
  { end: 90, display: '→ 10', unit: 'minutes', note: 'Morning ritual compressed from 90 to 10' },
  { end: 12, display: 'M+', unit: 'kirana shops', note: 'General Trade: 85% of all FMCG sales in India' },
  { end: 5, display: '', unit: 'entities tracked', note: 'Salesman, Retailer, SKU, Distributor, Beat' },
  { end: 28, display: '', unit: 'scheme templates', note: 'Pre-built with ROI benchmarks and targeting' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export default function Stats() {
  return (
    <section className="relative overflow-hidden border-y border-border bg-white py-24 sm:py-32" aria-labelledby="stats-heading">
      {/* Warm gradient wash */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(ellipse at 10% 80%, hsl(30 95% 52% / 0.04), transparent 50%), radial-gradient(ellipse at 90% 20%, hsl(160 60% 35% / 0.03), transparent 50%)',
        }}
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.p
          id="stats-heading"
          className="mx-auto max-w-3xl text-center font-serif text-2xl font-700 leading-snug text-foreground sm:text-3xl md:text-4xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          variants={fadeUp}
        >
          85% of India&apos;s FMCG sales flow through General Trade.{' '}
          <span className="text-muted-foreground">
            Most of it still runs on paper.
          </span>
        </motion.p>

        <div className="mt-16 grid grid-cols-2 gap-x-8 gap-y-12 sm:mt-20 lg:grid-cols-4">
          {stats.map((stat, i) => (
            <StatCard key={stat.unit} stat={stat} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({
  stat,
  index,
}: {
  stat: (typeof stats)[number];
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { value } = useCountUp({ end: stat.end, duration: 1600, enabled: inView });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      variants={fadeUp}
    >
      <p className="font-serif text-5xl font-700 tabular-nums text-foreground sm:text-6xl md:text-7xl">
        {value}
        <span className="text-accent/60">{stat.display}</span>
      </p>
      <p className="mt-2 text-sm font-semibold text-foreground">{stat.unit}</p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">{stat.note}</p>
    </motion.div>
  );
}
